/**
 * Safety assistant API client.
 *
 * Wraps axios with the platform's REACT_APP_BACKEND_URL + /api/assistant prefix
 * and a JWT interceptor, mirroring the platform's existing axios usage
 * (see services/lcaClient.js).
 *
 * Usage:
 *   import assistantClient from '@/services/assistantClient';
 *   const { data } = await assistantClient.post('/conversations', { title });
 *   await assistantClient.post(`/conversations/${id}/messages`, { content });
 */

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const assistantClient = axios.create({
  baseURL: `${BACKEND_URL}/api/assistant`,
  headers: { 'Content-Type': 'application/json' },
});

assistantClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

assistantClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Stream an assistant answer via Server-Sent Events.
 *
 * axios can't expose a streaming body in the browser, so this uses fetch +
 * ReadableStream, mirroring the platform's existing SSE consumer
 * (pages/sustainability/BRSRModule.js). Callbacks:
 *   onDelta(text)  — a chunk of the answer as it is generated
 *   onDone(evt)    — terminal event { answer, message_id, tools_used, ... }
 *   onError(evt)   — { status, message } on failure
 *
 * @returns {Promise<void>} resolves when the stream ends.
 */
export async function streamMessage(conversationId, content, { onDelta, onDone, onError, signal } = {}) {
  const token = localStorage.getItem('token');
  let resp;
  try {
    resp = await fetch(`${BACKEND_URL}/api/assistant/conversations/${conversationId}/messages/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ content }),
      signal,
    });
  } catch (e) {
    onError?.({ status: 0, message: 'Network error' });
    return;
  }

  if (resp.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    return;
  }
  if (!resp.ok || !resp.body) {
    onError?.({ status: resp.status, message: 'Request failed' });
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith('data:')) continue;
      let evt;
      try {
        evt = JSON.parse(line.slice(5).trim());
      } catch (e) {
        continue;
      }
      if (evt.type === 'delta') onDelta?.(evt.text || '');
      else if (evt.type === 'done') onDone?.(evt);
      else if (evt.type === 'error') onError?.(evt);
    }
  }
}

/**
 * Request an AI prefill draft for a module's create form.
 * Resolves to { generation_id, draft, rationale, field_sources, certainty,
 * sources_used, warnings } — may take 5–60s; pass an AbortController signal.
 */
export async function requestPrefill(module, { description, documentFileIds = [] }, { signal } = {}) {
  const { data } = await assistantClient.post(
    `/prefill/${module}`,
    { description, document_file_ids: documentFileIds },
    { signal }
  );
  return data;
}

/**
 * Fetch deterministic "similar past records" suggestions for a create page.
 * Resolves to { module, results: [...], count }.
 */
export async function fetchSimilar(module, q, { signal, limit = 5 } = {}) {
  const { data } = await assistantClient.get(`/similar/${module}`, {
    params: { q, limit },
    signal,
  });
  return data;
}

/**
 * Fire-and-forget acceptance beacon after submitting a form that had AI
 * prefill. Field NAMES only — never values. Errors are swallowed: the
 * beacon must never affect the save flow.
 */
export function sendPrefillFeedback(payload) {
  return assistantClient.post('/prefill/feedback', payload).catch(() => {});
}

export default assistantClient;
